import { getTextFromHtmlString, strToUpperCaseFirst } from '@/Utility/utils';
import type { ZnglyVideos_Pagination_Fragment$data } from '@/graphql/queries/ZnglyVideo.query';
import type { ZnglyVideo } from '@/graphql/types';
import {
  type MediaItemSizeEnum,
  PostStatusEnum,
  ZnglyVideoMediaNameEnum,
  ZnglyVideoPostTypeEnum,
  type ZnglyVideo_Znglycontent,
} from '@/graphql/types';
import { type ZnMediaItem, getMediaItemUrl } from '.';

export const urlPattern = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+:]*)*' + // port and path
    '(\\?[?:;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
  'i'
);

export const isMobile = {
  Android: () => navigator.userAgent.match(/Android/i),
  BlackBerry: () => navigator.userAgent.match(/BlackBerry/i),
  iOS: () => navigator.userAgent.match(/iPhone|iPad|iPod/i),
  Opera: () => navigator.userAgent.match(/Opera Mini/i),
  Windows: () => navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i),
  any: () => isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Opera() || isMobile.Windows(),
};

export const getMediaNameFromMime = (mimeType: string): ZnglyVideoMediaNameEnum | null => {
  const mimeTypeParts = mimeType?.split('/');
  if (mimeTypeParts.length < 2) return null;
  const mime = mimeTypeParts[1];

  switch (mime) {
    case 'mp4':
      return ZnglyVideoMediaNameEnum.Mp4;
    case 'mp3':
    case 'mpeg':
      return ZnglyVideoMediaNameEnum.Audio;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return ZnglyVideoMediaNameEnum.Image;
    case 'pdf':
      return ZnglyVideoMediaNameEnum.Pdf;
    case 'html':
      return ZnglyVideoMediaNameEnum.Link;
    case '':
    case null:
    case undefined:
      return ZnglyVideoMediaNameEnum.Undefined;
    default:
      return ZnglyVideoMediaNameEnum.Link;
  }
};

export const getUrlMediaName = async (
  _url: string,
  options?: { skipLink: boolean }
): Promise<ZnglyVideoMediaNameEnum | null> => {
  let url = _url;
  if (!url || url === '' || url === null) return ZnglyVideoMediaNameEnum.Undefined;

  let urlIsIframe = false;
  if (url.startsWith('<iframe')) {
    const dom = new DOMParser().parseFromString(url, 'text/html');
    const iframe = dom.querySelector('iframe');
    if (!!iframe?.src) {
      url = iframe.src;
      urlIsIframe = true;
    } else {
      return ZnglyVideoMediaNameEnum.Embed;
    }
  }

  let token = url.toLowerCase().split('.').slice(-1)[0];
  if (url.startsWith('data:')) {
    // split the data url to get the mime type: data:image/png;base64,...
    token = url?.split(':')?.[1]?.split(';')?.[0]?.split('/')?.[1];
  }

  // test for images
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].some((ext) => token.endsWith(ext))) return ZnglyVideoMediaNameEnum.Image;
  // test for unsupported image types
  if (['svg', 'bmp', 'tiff', 'tif', 'psd', 'ai', 'eps', 'ps', 'ico', 'webp'].some((ext) => token.endsWith(ext)))
    return ZnglyVideoMediaNameEnum.Undefined;

  // test for application types
  if (['pdf'].some((ext) => token.endsWith(ext))) return ZnglyVideoMediaNameEnum.Pdf;
  // test for unsupported application types
  if (
    ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pps', 'ppsx', 'odt', 'ods', 'rtf', 'txt'].some((ext) =>
      token.endsWith(ext)
    )
  )
    return ZnglyVideoMediaNameEnum.Undefined;

  // test for videos
  if (['mp4'].some((ext) => token.endsWith(ext))) return ZnglyVideoMediaNameEnum.Mp4;
  if (['mp3', 'mpeg'].some((ext) => token.endsWith(ext))) return ZnglyVideoMediaNameEnum.Audio;
  // test for unsupported video types
  if (['webm', 'ogv', 'flv', 'mov', 'avi', 'wmv', 'mpg', 'mpeg', 'm4v', '3gp'].some((ext) => token.endsWith(ext)))
    return ZnglyVideoMediaNameEnum.Undefined;

  // test for embeddable content
  if (url.indexOf('https://vimeo.com/') === 0) return ZnglyVideoMediaNameEnum.Vimeo;
  if (url.indexOf('https://player.vimeo.com/video/') === 0) return ZnglyVideoMediaNameEnum.Vimeo;
  if (url.indexOf('https://www.youtube.com/') === 0 || url.indexOf('https://youtu.be/') === 0)
    return ZnglyVideoMediaNameEnum.Youtube;
  if (url.indexOf('https://open.spotify.com/') === 0) return ZnglyVideoMediaNameEnum.Spotify;
  if (url.indexOf('https://www.ted.com/') === 0) return ZnglyVideoMediaNameEnum.Ted;
  if (url.indexOf('https://soundcloud.com/') === 0) return ZnglyVideoMediaNameEnum.Soundcloud;
  if (url.indexOf('https://www.mixcloud.com/') === 0) return ZnglyVideoMediaNameEnum.Mixcloud;
  if (url.indexOf('https://share.synthesia.io/') === 0) return ZnglyVideoMediaNameEnum.Synthesia;

  // test for sharepoint. only http or s . *.sharepoint .com
  if (url.match(/^(http|https):\/\/[a-z0-9-]+\.sharepoint\.com/)) return ZnglyVideoMediaNameEnum.Sharepoint;
  if (urlIsIframe) return ZnglyVideoMediaNameEnum.Embed;

  if (options?.skipLink === true) return ZnglyVideoMediaNameEnum.Undefined;

  if (!!urlPattern.test(url)) {
    // smart workaround for content which is hidden behind a query string
    try {
      const _url = new URL(url);
      const bareUrl = _url.origin + _url.pathname; // remove query string

      const urlMediaName = await getUrlMediaName(bareUrl, { skipLink: true }); // run through this function again
      if (urlMediaName !== ZnglyVideoMediaNameEnum.Undefined) return urlMediaName; // if we get a valid media name, return it
    } catch (error) {
      //
    }

    // test the link response to see its actual content type
    try {
      const response = await fetch(url, {
        referrer: 'no-referrer',
        referrerPolicy: 'no-referrer',
        mode: 'no-cors',
      });

      const contentType = response.headers.get('content-type') || response.headers.get('Content-Type');
      if (contentType) return getMediaNameFromMime(contentType);
    } catch (error) {
      //
    }

    return ZnglyVideoMediaNameEnum.Link;
  }

  return ZnglyVideoMediaNameEnum.Undefined;
};

export type MediaType = 'PDF' | 'MP4' | 'UNDEFINED' | 'IMAGE' | 'EMBED' | 'LINK' | 'AUDIO';

export const getAggregatedMediaTypeFromMediaName = (contentName: ZnglyVideoMediaNameEnum | null): MediaType => {
  switch (contentName) {
    case ZnglyVideoMediaNameEnum.Mp4:
      return 'MP4';
    case ZnglyVideoMediaNameEnum.Pdf:
      return 'PDF';
    case ZnglyVideoMediaNameEnum.Image:
      return 'IMAGE';
    case ZnglyVideoMediaNameEnum.Audio:
      return 'AUDIO';
    case ZnglyVideoMediaNameEnum.Vimeo:
    case ZnglyVideoMediaNameEnum.Youtube:
    case ZnglyVideoMediaNameEnum.Spotify:
    case ZnglyVideoMediaNameEnum.Ted:
    case ZnglyVideoMediaNameEnum.Soundcloud:
    case ZnglyVideoMediaNameEnum.Mixcloud:
    case ZnglyVideoMediaNameEnum.Synthesia:
    case ZnglyVideoMediaNameEnum.Sharepoint:
    case ZnglyVideoMediaNameEnum.Embed:
      return 'EMBED';
    case ZnglyVideoMediaNameEnum.Link:
      return 'LINK';
    default:
      return 'UNDEFINED';
  }
};

/**
 * Get the player link and featured image for a given ZnglyContent
 * @param znglyContent
 * @param preferredSize - optional preferred size for the media item
 */
export const getCatalogMediaLinks = (
  znglyContent: ZnglyVideo_Znglycontent | null | undefined,
  preferredSize?: MediaItemSizeEnum
) => {
  if (!znglyContent) return { srcLink: null, featuredImage: null };

  const contentMediaType = znglyContent?.contentMediaType;

  let srcLink: string | null = null;
  if (contentMediaType === ZnglyVideoPostTypeEnum.Embed || contentMediaType === ZnglyVideoPostTypeEnum.External)
    srcLink = znglyContent?.contentLink ?? null;
  else if (contentMediaType === ZnglyVideoPostTypeEnum.File) srcLink = znglyContent?.contentFile?.mediaItemUrl ?? null;

  const featuredImage = getCatalogMediaFeaturedImage(znglyContent, preferredSize);

  return { srcLink, featuredImage };
};

export function mediaTypeNameHandler(_contentMediaName: string | null | undefined) {
  if (!_contentMediaName || _contentMediaName === '') return '';
  let contentMediaName = _contentMediaName?.toLowerCase();

  if (contentMediaName === 'youtube') contentMediaName = 'YouTube';
  else if (contentMediaName === 'soundcloud') contentMediaName = 'SoundCloud';
  else if (contentMediaName === 'ted') contentMediaName = 'TED';
  else if (contentMediaName === 'pdf') contentMediaName = 'PDF';
  else if (contentMediaName === 'mp4') contentMediaName = 'MP4';
  else contentMediaName = strToUpperCaseFirst(contentMediaName);

  return contentMediaName;
}

type VisibilityReturnTypes = 'Published' | 'Draft' | 'Pending' | 'Private' | string;

// @todo: remove this function once refactored
export const prettyVisibility = (visibility: string | null | undefined): VisibilityReturnTypes => {
  if (!visibility) return '';
  const upperStatus = String(visibility).toUpperCase();

  if (upperStatus === PostStatusEnum.Publish) {
    return 'Published';
  }
  if (upperStatus === PostStatusEnum.Hidden) {
    return 'Private';
  }

  return upperStatus.slice(0, 1).toUpperCase() + visibility.toLowerCase().slice(1);
};

export const getCatalogMediaFeaturedImage = (
  znglyContent:
    | ZnglyVideo_Znglycontent
    | Partial<
        NonNullable<ZnglyVideos_Pagination_Fragment$data['znglyVideos']>['edges'][number]['node']['znglyContent']
      >,
  preferredSize?: MediaItemSizeEnum
): string | null => {
  const contentMediaType = znglyContent?.contentMediaType;
  const contentMediaName = znglyContent?.contentMediaName;

  let featImg: string | null =
    getMediaItemUrl(znglyContent?.featuredImageFile as ZnMediaItem, preferredSize) ??
    znglyContent?.featuredImageLink ??
    null;

  if (!!featImg) return featImg;

  // if no featured image, try to get one from the content
  if (contentMediaType === ZnglyVideoPostTypeEnum.File && contentMediaName === ZnglyVideoMediaNameEnum.Image)
    featImg = getMediaItemUrl(znglyContent?.contentFile as ZnMediaItem, preferredSize);
  else if (contentMediaType === ZnglyVideoPostTypeEnum.External && contentMediaName === ZnglyVideoMediaNameEnum.Image)
    featImg = znglyContent?.contentLink ?? null;

  return featImg;
};

/**
 * Get the description of a ZnglyVideo node
 */
export const getDescription = (
  node: ZnglyVideo | null | undefined
): {
  hasAiResult: boolean;
  description: string;
  aiExtractiveAnswer?: string;
  aiSnippet?: string;
} => {
  const desc = getTextFromHtmlString(node?.znglyContent?.htmlDesc ?? node?.znglyContent?.shortText ?? '').text;

  const aiExtractiveAnswer =
    getTextFromHtmlString(node?.searchResults?.extractiveAnswers?.map((answer) => answer?.content).join('. ') ?? '')
      .text ?? null;

  let aiSnippet =
    getTextFromHtmlString(node?.searchResults?.snippets?.map((snippet) => snippet?.snippet).join('. ') ?? '').text ??
    null;
  if (!!aiSnippet && aiSnippet.toLowerCase().includes('no snippet is available')) {
    aiSnippet = '';
  }

  return {
    hasAiResult: !!node?.searchResults,
    description: desc,
    aiExtractiveAnswer,
    aiSnippet,
  };
};
